import SEO from '../components/SEO';
import React, { useState } from 'react';
import { mockData } from '../mock';
import * as LucideIcons from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [quoteForm, setQuoteForm] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    howDidYouFindUs: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/quotes`, quoteForm);
      toast.success('Quote request submitted! We\'ll contact you within 24 hours.');
      setQuoteForm({
        name: '',
        email: '',
        phone: '',
        service: '',
        howDidYouFindUs: '',
        message: ''
      });
    } catch (error) {
      toast.error('Failed to submit quote. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Contact Us - Free Quote Ipswich Builders"
        description="Contact T&M Construction for a FREE quote. Builders in Ipswich, Suffolk. Call +44 333 335 5532 or email info@tandmbuilders.co.uk. Kitchen, bathroom, extensions."
        keywords="contact builders Ipswich, free quote construction Suffolk, builders phone number, construction company email, get quote renovation, builders near me contact"
        canonical="/contact"
      />
      <section className="pt-56 pb-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">Contact Us</h1>
            <p className="text-xl text-gray-600">
              Ready to start your project? Get in touch for a free consultation and quote
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Get In Touch</h2>
              <p className="text-gray-600 mb-8">
                Have a question or ready to discuss your project? We're here to help. Fill out the form or use one of the contact methods below.
              </p>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-orange-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <LucideIcons.Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="font-semibold text-lg text-gray-900">Phone</div>
                    <a href={`tel:${mockData.company.phone}`} className="text-orange-600 hover:underline">{mockData.company.phone}</a>
                    <p className="text-sm text-gray-500 mt-1">Mon-Fri 8am-6pm, Sat 9am-4pm</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-orange-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <LucideIcons.Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="font-semibold text-lg text-gray-900">Email</div>
                    <a href={`mailto:${mockData.company.email}`} className="text-orange-600 hover:underline">{mockData.company.email}</a>
                    <p className="text-sm text-gray-500 mt-1">We'll respond within 24 hours</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-orange-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <LucideIcons.MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="font-semibold text-lg text-gray-900">Location</div>
                    <p className="text-gray-600">{mockData.company.address}</p>
                    <p className="text-sm text-gray-500 mt-1">Serving Suffolk, Essex & London</p>
                  </div>
                </div>
              </div>

              <div className="mt-12 p-6 bg-orange-50 rounded-xl border-2 border-orange-200">
                <h3 className="font-semibold text-lg text-gray-900 mb-3">Service Areas</h3>
                <div className="grid grid-cols-2 gap-3">
                  {['Ipswich', 'Colchester', 'Woodbridge', 'Felixstowe', 'Bury St Edmunds', 'Chelmsford'].map((area) => (
                    <div key={area} className="flex items-center space-x-2">
                      <LucideIcons.CheckCircle2 className="w-4 h-4 text-orange-600 flex-shrink-0" />
                      <span className="text-gray-700 text-sm">{area}</span>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-gray-500 mt-3">+ All of Suffolk, Essex & London</p>
              </div>
            </div>

            <div>
              <Card className="shadow-2xl">
                <CardHeader>
                  <CardTitle className="text-2xl">Request a Free Quote</CardTitle>
                  <CardDescription>Fill out the form and we'll get back to you within 24 hours</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleQuoteSubmit} className="space-y-4">
                    <div>
                      <Input 
                        placeholder="Your Name"
                        value={quoteForm.name}
                        onChange={(e) => setQuoteForm({...quoteForm, name: e.target.value})}
                        required
                        className="border-gray-300"
                      />
                    </div>
                    <div>
                      <Input 
                        type="email"
                        placeholder="Your Email"
                        value={quoteForm.email}
                        onChange={(e) => setQuoteForm({...quoteForm, email: e.target.value})}
                        required
                        className="border-gray-300"
                      />
                    </div>
                    <div>
                      <Input 
                        type="tel"
                        placeholder="Your Phone"
                        value={quoteForm.phone}
                        onChange={(e) => setQuoteForm({...quoteForm, phone: e.target.value})}
                        required
                        className="border-gray-300"
                      />
                    </div>
                    <div>
                      <Input 
                        placeholder="Service Needed (e.g., Kitchen Renovation)"
                        value={quoteForm.service}
                        onChange={(e) => setQuoteForm({...quoteForm, service: e.target.value})}
                        required
                        className="border-gray-300"
                      />
                    </div>
                    <div>
                      <select
                        value={quoteForm.howDidYouFindUs}
                        onChange={(e) => setQuoteForm({...quoteForm, howDidYouFindUs: e.target.value})}
                        required
                        className="w-full h-10 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white"
                      >
                        <option value="">How did you find us?</option>
                        <option value="Google Search">Google Search</option>
                        <option value="Facebook">Facebook</option>
                        <option value="Instagram">Instagram</option>
                        <option value="Recommendation">Recommendation from friend/family</option>
                        <option value="Drove/Walked Past">Drove/Walked Past</option>
                        <option value="Previous Customer">Previous Customer</option>
                        <option value="MyBuilder">MyBuilder</option>
                        <option value="Checkatrade">Checkatrade</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div>
                      <Textarea 
                        placeholder="Tell us about your project..."
                        value={quoteForm.message}
                        onChange={(e) => setQuoteForm({...quoteForm, message: e.target.value})}
                        required
                        rows={5}
                        className="border-gray-300"
                      />
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full bg-orange-600 hover:bg-orange-700 text-white"
                      size="lg"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <LucideIcons.Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <LucideIcons.Send className="w-5 h-5 mr-2" />
                          Submit Request
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;